import { useEffect, useRef, useState, type FormEvent } from "react";
import { Link, useNavigate, useLocation, useSearchParams } from "react-router";
import { useQueryClient } from "@tanstack/react-query";
import { AppCanvas } from "@/components/glass/AppCanvas";
import { QRCodeSVG } from "qrcode.react";
import { login, loginMfa, loginMfaEnroll, magicLogin } from "@/lib/api";
import { consumeSessionExpired, type LoginResponse } from "@/lib/auth";
import {
  firstAccessibleRoute,
  NO_ACCESS_ROUTE,
} from "@/lib/firstAccessibleRoute";
import orikinWordmark from "@/assets/brand/orikin-wordmark-on-dark.png";
import "@/pages/login.css";

// §v3.login-aurora-3d (2026-05-14) — XPAND admin sign-in.
//
// Visual stack (back → front):
//   <AppCanvas/> handles the backdrop (CSS canvas-gradient FCP paint +
//   LiquidMetal shader at fit=cover scale=2.5 + two ambient drifting
//   orbs). Identical to the dashboard backdrop so the login → app
//   transition is visually seamless.
//   On top: the .lp-card glass panel with the form.

export function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const stageRef = useRef<HTMLDivElement | null>(null);
  const fromPath =
    (location.state as { from?: { pathname: string } } | null)?.from?.pathname ??
    null;

  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [bootstrapSecret, setBootstrapSecret] = useState("");
  const [showBootstrap, setShowBootstrap] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  // §security.session-expired (2026-06-20) — if we arrived here because the API
  // layer flagged an expired session (401 + failed silent refresh), show a clear
  // notice instead of a blank form. Consumed once (cleared on read).
  const [sessionExpired, setSessionExpired] = useState(false);
  useEffect(() => {
    if (consumeSessionExpired()) setSessionExpired(true);
  }, []);
  // §aurora-glass (2026-06-16) — real backend-health probe behind the footer
  // status pill (was a hardcoded "All systems operational"). Hits the public
  // /healthz endpoint (same-origin in prod; dev-proxied via vite.config) on
  // mount + every 30s, with a 6s timeout so a hung request can't pin it.
  const [health, setHealth] = useState<"checking" | "ok" | "down">("checking");
  // §5.1-bug2 — consume a first-login magic token from ?magic=... on
  // mount. The wizard's Go Live step hands the operator a link shaped
  // `/login?magic=<token>`. One-shot, 24h TTL.
  const [magicState, setMagicState] = useState<"idle" | "consuming">("idle");
  const magicAttemptedRef = useRef(false);
  // §v3.mfa — second-factor challenge. When login() returns
  // mfa_required we hold the one-time challenge here and render the
  // 6-digit code step instead of completing the sign-in.
  const [mfaChallenge, setMfaChallenge] = useState<string | null>(null);
  const [mfaCode, setMfaCode] = useState("");
  const [mfaError, setMfaError] = useState<string | null>(null);
  const [mfaSubmitting, setMfaSubmitting] = useState(false);
  // §security.mandatory-mfa (2026-06-20) — enroll-on-login: when the tenant
  // requires 2FA and this operator isn't enrolled, login returns the QR data and
  // we walk them through setup right here (scan → code → save backup codes → in).
  const [mfaEnroll, setMfaEnroll] = useState<{
    challenge: string;
    otpauthUri: string;
    secret: string;
  } | null>(null);
  const [enrollCode, setEnrollCode] = useState("");
  const [enrollError, setEnrollError] = useState<string | null>(null);
  const [enrollSubmitting, setEnrollSubmitting] = useState(false);
  const [backupCodes, setBackupCodes] = useState<string[] | null>(null);
  const [pendingLogin, setPendingLogin] = useState<LoginResponse | null>(null);

  useEffect(() => {
    const token = searchParams.get("magic");
    if (!token || magicAttemptedRef.current) return;
    magicAttemptedRef.current = true;
    setMagicState("consuming");
    setError(null);
    void (async () => {
      try {
        const result = await magicLogin(token);
        // Strip ?magic= before navigating so a browser back-nav
        // doesn't replay the (now-consumed) token.
        const next = new URLSearchParams(searchParams);
        next.delete("magic");
        setSearchParams(next, { replace: true });
        // §onboarding-activation (decision #3) — a first-login Owner is on a
        // random bootstrap password; send them to set one before the dashboard.
        navigate(result?.needsPasswordSet ? "/set-password" : "/inbox", {
          replace: true,
        });
      } catch (err) {
        const message = err instanceof Error ? err.message : "Magic link failed";
        setError(message);
        // Strip the token so a refresh doesn't retry the same dead
        // link — the operator can still sign in manually below.
        const next = new URLSearchParams(searchParams);
        next.delete("magic");
        setSearchParams(next, { replace: true });
      } finally {
        setMagicState("idle");
      }
    })();
  }, [searchParams, setSearchParams, navigate]);

  // §aurora-glass (2026-06-16) — probe the public /healthz endpoint so the
  // footer status pill is real, not a hardcoded "operational". Checks on
  // mount + every 30s; a 6s AbortController timeout means a hung request
  // resolves to "down" rather than pinning the pill on "checking".
  useEffect(() => {
    let cancelled = false;
    const check = async () => {
      const ctrl = new AbortController();
      const timer = window.setTimeout(() => ctrl.abort(), 6000);
      try {
        const res = await fetch("/healthz", {
          signal: ctrl.signal,
          cache: "no-store",
        });
        if (!cancelled) setHealth(res.ok ? "ok" : "down");
      } catch {
        if (!cancelled) setHealth("down");
      } finally {
        window.clearTimeout(timer);
      }
    };
    void check();
    const id = window.setInterval(() => void check(), 30_000);
    return () => {
      cancelled = true;
      window.clearInterval(id);
    };
  }, []);

  // Shared post-authentication routing (used by both the password path
  // and the MFA-completion path). §rbac — invalidateQueries wipes a
  // previous operator's cached /me perms; §v3.profile-picker — fresh
  // logins route through /select-profile, deep-links honour ``from``.
  const completeLogin = async (response: LoginResponse) => {
    await queryClient.invalidateQueries();
    const fresh = !fromPath || fromPath === "/login" || fromPath === "/";
    if (fresh) {
      navigate("/select-profile", { replace: true });
    } else {
      const perms = response.permissions ?? [];
      const accessible = firstAccessibleRoute(perms);
      const destination =
        accessible !== NO_ACCESS_ROUTE ? fromPath : accessible;
      navigate(destination, { replace: true });
    }
  };

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    if (submitting) return;
    setError(null);
    setSubmitting(true);
    try {
      const response = await login(
        username.trim(),
        password,
        bootstrapSecret.trim() || undefined,
      );
      // §v3.mfa — if the account has 2FA on, the response carries no
      // tokens (login() persisted nothing); switch to the code step and
      // finish the sign-in in handleMfaSubmit.
      if (response.mfa_required && response.mfa_challenge) {
        setMfaChallenge(response.mfa_challenge);
        return;
      }
      // §security.mandatory-mfa — tenant requires 2FA + not enrolled → set up now.
      if (response.mfa_enrollment_required && response.mfa_challenge) {
        setMfaEnroll({
          challenge: response.mfa_challenge,
          otpauthUri: response.otpauth_uri ?? "",
          secret: response.secret ?? "",
        });
        return;
      }
      await completeLogin(response);
    } catch (err) {
      const message = err instanceof Error ? err.message : "Login failed";
      setError(message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleMfaSubmit = async (event: FormEvent) => {
    event.preventDefault();
    if (mfaSubmitting || !mfaChallenge) return;
    setMfaError(null);
    setMfaSubmitting(true);
    try {
      const response = await loginMfa(mfaChallenge, mfaCode.trim());
      await completeLogin(response);
    } catch (err) {
      setMfaError(err instanceof Error ? err.message : "Verification failed");
    } finally {
      setMfaSubmitting(false);
    }
  };

  const handleEnrollSubmit = async (event: FormEvent) => {
    event.preventDefault();
    if (enrollSubmitting || !mfaEnroll) return;
    setEnrollError(null);
    setEnrollSubmitting(true);
    try {
      const response = await loginMfaEnroll(mfaEnroll.challenge, enrollCode.trim());
      // Show the one-time backup codes before routing into the app; if the
      // backend somehow didn't return any, just continue.
      if (response.backup_codes?.length) {
        setBackupCodes(response.backup_codes);
        setPendingLogin(response);
      } else {
        await completeLogin(response);
      }
    } catch (err) {
      setEnrollError(err instanceof Error ? err.message : "Verification failed");
    } finally {
      setEnrollSubmitting(false);
    }
  };

  if (magicState === "consuming") {
    return (
      <div
        ref={stageRef}
        className="lp-stage"
        role="status"
        aria-live="polite"
      >
        <AppCanvas variant="login" />
        <div className="lp-magic">
          <img
            src={orikinWordmark}
            alt="ORIKIN AI"
            className="lp-wordmark"
            style={{ height: 28 }}
          />
          <div className="lp-magic-text">
            <span className="lp-submit-spinner" aria-hidden="true" />
            Signing you in…
          </div>
        </div>
      </div>
    );
  }

  // §security.mandatory-mfa — after enrolling at login, show the one-time backup
  // codes before routing into the app.
  if (backupCodes) {
    return (
      <div ref={stageRef} className="lp-stage">
        <AppCanvas variant="login" />
        <div className="lp-card">
          <header className="lp-head">
            <img
              src={orikinWordmark}
              alt="ORIKIN AI"
              className="lp-wordmark"
              draggable={false}
            />
            <div className="lp-subhead">Save your backup codes</div>
          </header>
          <div className="lp-form">
            <p style={{ fontSize: 13, opacity: 0.75 }}>
              Store these somewhere safe. Each one works once if you ever lose
              your authenticator. They won&rsquo;t be shown again.
            </p>
            <div className="lp-backup-codes">
              {backupCodes.map((c) => (
                <code key={c}>{c}</code>
              ))}
            </div>
            <button
              type="button"
              className="lp-submit"
              onClick={() => {
                if (pendingLogin) void completeLogin(pendingLogin);
              }}
            >
              I&rsquo;ve saved them — continue
            </button>
          </div>
        </div>
      </div>
    );
  }

  // §security.mandatory-mfa — enroll-on-login: scan the QR + enter the first code.
  if (mfaEnroll) {
    return (
      <div ref={stageRef} className="lp-stage">
        <AppCanvas variant="login" />
        <form className="lp-card" onSubmit={handleEnrollSubmit} noValidate>
          <header className="lp-head">
            <img
              src={orikinWordmark}
              alt="ORIKIN AI"
              className="lp-wordmark"
              draggable={false}
            />
            <div className="lp-subhead">Set up two-factor authentication</div>
          </header>
          <div className="lp-form">
            <p style={{ fontSize: 13, opacity: 0.75 }}>
              Your workspace requires 2FA. Scan this with an authenticator app
              (Google Authenticator, 1Password, Authy…), then enter the 6-digit
              code to finish and sign in.
            </p>
            {mfaEnroll.otpauthUri ? (
              <div className="lp-qr">
                <QRCodeSVG value={mfaEnroll.otpauthUri} size={168} />
              </div>
            ) : null}
            {mfaEnroll.secret ? (
              <p
                style={{
                  fontSize: 12,
                  opacity: 0.6,
                  textAlign: "center",
                  wordBreak: "break-all",
                }}
              >
                Can&rsquo;t scan? Enter this key: <code>{mfaEnroll.secret}</code>
              </p>
            ) : null}
            <div className="lp-field">
              <label htmlFor="enroll-code" className="lp-label">
                Authentication code
              </label>
              <input
                id="enroll-code"
                name="enroll-code"
                inputMode="numeric"
                autoComplete="one-time-code"
                autoFocus
                required
                value={enrollCode}
                onChange={(e) => setEnrollCode(e.target.value)}
                placeholder="123 456"
                className="lp-input"
                aria-invalid={enrollError ? "true" : undefined}
              />
            </div>
            {enrollError ? (
              <div role="alert" className="lp-error">
                {enrollError}
              </div>
            ) : null}
            <button
              type="submit"
              className="lp-submit"
              disabled={enrollSubmitting}
              aria-busy={enrollSubmitting || undefined}
            >
              {enrollSubmitting ? (
                <>
                  <span className="lp-submit-spinner" aria-hidden="true" />
                  Verifying…
                </>
              ) : (
                "Verify & finish sign-in"
              )}
            </button>
          </div>
        </form>
      </div>
    );
  }

  if (mfaChallenge) {
    return (
      <div ref={stageRef} className="lp-stage">
        <AppCanvas variant="login" />
        <form className="lp-card" onSubmit={handleMfaSubmit} noValidate>
          <header className="lp-head">
            <img
              src={orikinWordmark}
              alt="ORIKIN AI"
              className="lp-wordmark"
              draggable={false}
            />
            <div className="lp-subhead">Two-factor authentication</div>
          </header>
          <div className="lp-form">
            <p style={{ fontSize: 13, opacity: 0.75 }}>
              Enter the 6-digit code from your authenticator app. You can also
              use one of your backup codes.
            </p>
            <div className="lp-field">
              <label htmlFor="mfa-code" className="lp-label">
                Authentication code
              </label>
              <input
                id="mfa-code"
                name="mfa-code"
                inputMode="numeric"
                autoComplete="one-time-code"
                autoFocus
                required
                value={mfaCode}
                onChange={(e) => setMfaCode(e.target.value)}
                placeholder="123 456"
                className="lp-input"
                aria-invalid={mfaError ? "true" : undefined}
              />
            </div>
            {mfaError ? (
              <div role="alert" className="lp-error">
                {mfaError}
              </div>
            ) : null}
            <button
              type="submit"
              className="lp-submit"
              disabled={mfaSubmitting}
              aria-busy={mfaSubmitting || undefined}
            >
              {mfaSubmitting ? (
                <>
                  <span className="lp-submit-spinner" aria-hidden="true" />
                  Verifying…
                </>
              ) : (
                "Verify"
              )}
            </button>
            <button
              type="button"
              className="lp-bootstrap-toggle"
              style={{ textAlign: "center", display: "block" }}
              onClick={() => {
                setMfaChallenge(null);
                setMfaCode("");
                setMfaError(null);
              }}
            >
              ← Back to sign in
            </button>
          </div>
        </form>
      </div>
    );
  }

  return (
    <div ref={stageRef} className="lp-stage">
      <AppCanvas variant="login" />

      <form className="lp-card" onSubmit={handleSubmit} noValidate>
        <header className="lp-head">
          <img
            src={orikinWordmark}
            alt="ORIKIN AI"
            className="lp-wordmark"
            draggable={false}
          />
          <div className="lp-tagline">AGENTS</div>
        </header>

        {sessionExpired && !error ? (
          <div role="status" className="lp-notice">
            Your session has expired. Please sign in again.
          </div>
        ) : null}

        <div className="lp-form">
          <div className="lp-field">
            <label htmlFor="username" className="lp-label">
              Username
            </label>
            <input
              id="username"
              name="username"
              autoComplete="username"
              autoFocus
              required
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              placeholder="operator"
              className="lp-input"
              aria-invalid={error ? "true" : undefined}
            />
          </div>

          <div className="lp-field">
            <label htmlFor="password" className="lp-label">
              Password
            </label>
            <input
              id="password"
              name="password"
              type="password"
              autoComplete="current-password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="lp-input"
              aria-invalid={error ? "true" : undefined}
            />
          </div>

          {showBootstrap ? (
            <div className="lp-field">
              <label htmlFor="admin-secret" className="lp-label">
                Admin bootstrap key
              </label>
              <input
                id="admin-secret"
                name="admin-secret"
                type="password"
                autoComplete="off"
                value={bootstrapSecret}
                onChange={(e) => setBootstrapSecret(e.target.value)}
                placeholder="required only for first operator"
                className="lp-input"
              />
            </div>
          ) : (
            <button
              type="button"
              className="lp-bootstrap-toggle"
              onClick={() => setShowBootstrap(true)}
            >
              First operator? Use a bootstrap key
            </button>
          )}

          {error ? (
            <div role="alert" className="lp-error">
              {error}
            </div>
          ) : null}

          <button
            type="submit"
            className="lp-submit"
            disabled={submitting}
            aria-busy={submitting || undefined}
          >
            {submitting ? (
              <>
                <span className="lp-submit-spinner" aria-hidden="true" />
                Signing in…
              </>
            ) : (
              "Sign in"
            )}
          </button>

          <Link
            to="/forgot-password"
            className="lp-bootstrap-toggle"
            style={{ textAlign: "center", display: "block" }}
          >
            Forgot password?
          </Link>

          {/* §account-moment — the wizard links here ("Already have a
              workspace?"); this is the return edge of that loop. */}
          <Link
            to="/get-started"
            className="lp-bootstrap-toggle"
            style={{ textAlign: "center", display: "block" }}
          >
            New to ORIKIN? Create your workspace
          </Link>
        </div>

        <footer className="lp-foot">
          <div className="lp-status" data-health={health}>
            <span className="lp-status-dot" aria-hidden="true" />
            {health === "ok"
              ? "All systems operational"
              : health === "down"
                ? "Service unavailable"
                : "Checking system status…"}
          </div>
          <div className="lp-meta">ORIKIN AI · admin</div>
        </footer>
      </form>
    </div>
  );
}
